import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { toggleBGM } from '../../redux/action/action';
import ClearCashButTransfer from '../ClearCash/ClearCashButTransfer';
import CommonConfirmButton from '../Common/CommonConfirmButton';
import CommonModal from '../Common/CommonModal';
import { appName } from '@/config';

const EndGameDetail = ({
  onClose,
  previousBalance,
  transCode,
  withdrawalNumber
}) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);

  const memberInfo = useSelector((state) => state.isMemberInfo);
  const [currentTime] = useState(dayjs().format('YYYY-MM-DD HH:mm:ss'));
  const navigate = useNavigate();

  const isBGMEnabled = useSelector((state) => state.isBGMEnabled);
  const dispatch = useDispatch();

  const captureRef = useRef(null);
  const [screenshot, setScreenshot] = useState(null);
  const [isGenerating, setIsGenerating] = useState(true);

  // QRCode URL for verification (as per doc)
  const verificationUrl = useMemo(() => {
    return `${window.location.origin}/cash-register/order/${withdrawalNumber}`;
  }, [withdrawalNumber]);

  // Line Giveaway QRCode URL
  const lineGiveupQrCodeUrl = useMemo(() => {
    return `https://liff.line.me/2008392583-7DX5AyA8?withdrawal_number=${
      withdrawalNumber || ''
    }`;
  }, [withdrawalNumber]);

  useEffect(() => {
    const generateScreenshot = async () => {
      if (captureRef.current) {
        try {
          // 延遲 800ms 確保樣式與背景加載完成
          await new Promise((resolve) => setTimeout(resolve, 800));

          const canvas = await html2canvas(captureRef.current, {
            scale: 2,
            useCORS: true,
            backgroundColor: null, // 移除背景色設定
            onclone: (clonedDoc) => {
              // 處理 html2canvas 不支援 background-clip: text 的問題
              const clonedGradients = clonedDoc.getElementsByClassName(
                'screenshot-text-gradient'
              );
              for (let el of clonedGradients) {
                el.style.background = 'none';
                el.style.webkitBackgroundClip = 'initial';
                el.style.webkitTextFillColor = 'initial';
                el.style.color = '#fdd26a';
              }

              // 強化 InfoRow 置中與樣式渲染
              const clonedBoxes = clonedDoc.getElementsByClassName(
                'screenshot-info-box'
              );
              for (let el of clonedBoxes) {
                el.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
                el.style.border = '1px solid #00E5FF';
                el.style.boxShadow = '0 0 3px #00E5FF';
                // el.style.display = 'block'; // 改用 block 配合 text-align 置中
                // el.style.textAlign = 'center';
                el.style.width = '100%';
                el.style.paddingBottom = '15px'; // 調整 padding 以適應置中
              }
            }
          });
          setScreenshot(canvas.toDataURL('image/png'));
        } catch (error) {
          console.error('Screenshot generation failed:', error);
        } finally {
          setIsGenerating(false);
        }
      }
    };

    generateScreenshot();
  }, []);

  // 處理確認按鈕
  const handleConfirm = () => {
    dispatch(
      toggleBGM({
        ...isBGMEnabled,
        song: 'back2_',
        musicVolume: 0.2
      })
    );
    navigate('/');
    onClose();
  };

  const InfoRow = ({ label, value, isGradient = false }) => (
    <Flex w={'100%'} alignItems={'center'} gap="10px">
      <Text fontSize={'14px'} color="#C5B3C7" minW="80px" textAlign="left">
        {label}
      </Text>
      <Box
        className="screenshot-info-box"
        flex={1}
        fontSize="18px"
        color="white"
        bg="rgba(0, 0, 0, 0.4)"
        borderRadius="15px"
        border="1px solid"
        borderColor="#00E5FF"
        boxShadow="0 0 8px #00E5FF, 0 0 16px #00E5FF, inset 0 0 6px rgba(0, 255, 255, 0.5)"
        // py="5px"
        // px="10px"
        textAlign="center"
      >
        <Text
          color="#fff"
          fontWeight="700"
          className={isGradient ? 'screenshot-text-gradient' : ''}
          display="inline-block"
        >
          {value}
        </Text>
      </Box>
    </Flex>
  );

  return (
    <CommonModal onConfirm={handleConfirm}>
      {/* QRcode遮罩 */}
      <ClearCashButTransfer onCancelClearCash={onClose} transCode={transCode} />

      {/* 1. 底層: 隱藏的擷取區域 (Snapshot Area) */}
      <Box
        ref={captureRef}
        position="absolute"
        left="-9999px"
        top="0"
        w="350px"
        p="25px"
        borderRadius="20px"
        overflow="hidden"
      >
        <Flex flexDirection="column" alignItems="center" gap={4}>
          <Box fontSize="16px" color="#C5B3C7" mb={2}>
            {currentTime}
          </Box>

          <InfoRow
            label={i18n('machineCode')}
            value={`<NO.${memberInfo?.uid || '-'}>`}
          />
          <InfoRow
            label={i18n('withdrawNumber')}
            value={withdrawalNumber || '-'}
          />

          <Box position="relative" w="100%">
            <InfoRow
              label={i18n('GAME RESULT')}
              value={previousBalance || 0}
              isGradient={true}
            />
            {/* 浮水印 (Watermark) */}
            <Box
              position="absolute"
              top="0%"
              left="50%"
              transform="translate(-50%, -50%) rotate(-15deg)"
              opacity={0.05}
              pointerEvents="none"
              whiteSpace="nowrap"
              fontSize="24px"
              fontWeight="bold"
              fontStyle="italic"
              color="white"
              zIndex={5}
            >
              {appName}-{i18n('fold')}:{previousBalance || 0}
            </Box>
          </Box>
        </Flex>
      </Box>

      {/* 2. 中層: 顯示層 (Display Layer) */}
      <Flex
        flexDirection="column"
        gap={2}
        justifyContent="center"
        position="relative"
        zIndex={2}
        w="100%"
        pt="25px"
        minH="300px"
      >
        {isGenerating ? (
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={4}
            flex={1}
          >
            <Spinner size="xl" color="cyan.400" />
          </Flex>
        ) : (
          <Box overflow="hidden">
            <Flex
              position="absolute"
              transform="translate(-50%, -50%)"
              top="-5%"
              left="50%"
              mt={2}
              direction="column"
              alignItems="center"
              gap={2}
            >
              <Box p={2} bg="white" borderRadius="8px">
                <QRCodeCanvas value={verificationUrl} size={90} />
              </Box>
              <Text fontSize="10px" color="#C5B3C7">
                {i18n('orderVerification')}
              </Text>
            </Flex>
            <img
              src={screenshot}
              alt="End Game Detail"
              style={{ width: '100%', display: 'block' }}
            />
          </Box>
        )}

        {/* 3. 頂層: 互動層 (Interactive Layer) */}
        <Flex
          gap={4}
          my={6}
          direction={'column'}
          justifyContent="center"
          alignItems="center"
          position="relative"
          zIndex={3}
        >
          <CommonConfirmButton onClick={handleConfirm} />
        </Flex>
      </Flex>
    </CommonModal>
  );
};

export default EndGameDetail;
